// overlayGroups.js — pure helpers that walk the rendered portal→fund→member→position[] tree
// (already fetched from GET /api/ledger/overview) to collect the native stream_ids a merge_fund /
// link_member overlay action needs to touch. No I/O, no re-derivation of financial figures —
// this only reads stream_id/merged/linked fields the bridge already computed and attached.

// Every native position stream_id under one fund node, including the member-level residual
// stream when a member has has_member_level_activity (its own stream_id, distinct from any
// keyed position). Used to fan out ONE merge_fund overlay per source stream when merging funds.
export function collectFundStreamIds(fund) {
  const ids = new Set();
  for (const m of fund?.members || []) {
    const positions = m.positions && m.positions.length ? m.positions : [m];
    for (const p of positions) {
      if (p.stream_id) ids.add(p.stream_id);
    }
    if (m.has_member_level_activity && m.stream_id) ids.add(m.stream_id);
  }
  return [...ids];
}

// Every native position stream_id under one member (used to fan out ONE link_member overlay
// per source stream when linking two members as the same person).
export function collectMemberStreamIds(member) {
  const ids = new Set();
  const positions = member?.positions && member.positions.length ? member.positions : [member];
  for (const p of positions) {
    if (p.stream_id) ids.add(p.stream_id);
  }
  if (member?.has_member_level_activity && member.stream_id) ids.add(member.stream_id);
  return [...ids];
}

// Stream ids within a fund that are currently rendered with merged:true (i.e. grouped in from
// another native fund via an active merge_fund overlay) — the set "Unmerge" must revert.
export function collectMergedStreamIds(fund) {
  const ids = [];
  for (const m of fund?.members || []) {
    const positions = m.positions && m.positions.length ? m.positions : [m];
    for (const p of positions) {
      if (p.merged && p.stream_id) ids.push(p.stream_id);
    }
  }
  return ids;
}

// Stream ids within a member that are currently rendered with linked:true (i.e. grouped in from
// another native member via an active link_member overlay) — the set "Unlink" must revert.
export function collectLinkedStreamIds(member) {
  const positions = member?.positions && member.positions.length ? member.positions : [member];
  return positions.filter((p) => p.linked && p.stream_id).map((p) => p.stream_id);
}
